import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  IconButton,
  Switch,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Alert,
  Snackbar,
  Autocomplete,
  Grid,
  Divider,
  Tooltip,
  CircularProgress,
  FormControlLabel,
  Collapse
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Upload as UploadIcon,
  AddCircleOutline as AddRowIcon,
  RemoveCircleOutline as RemoveRowIcon,
  Code as CodeIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AttachFile as AttachFileIcon
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import SubCategoryForm from '../../components/Category/SubCategoryForm';
import DeleteConfirmDialog from '../../components/Common/DeleteConfirmDialog';
import { subCategoryService } from '../../services/subCategoryService';
import { webCategoryService } from '../../services/webCategoryService';
import useResponsive from '../../hooks/useResponsive';
import PageHeaderLayout from '../../components/Layout/PageHeaderLayout';

const SubCategoryList = () => {
  const isMobile = useResponsive('down', 'sm');
  const menuDispatch = useDispatch();
  const navigate = useNavigate();

  // Set active menu on component mount
  useEffect(() => {
    menuDispatch({
      type: 'menuSelect',
      payload: 'web-subcategory'
    });
  }, []);

  // State management
  const [subCategories, setSubCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalRows, setTotalRows] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterActive, setFilterActive] = useState(null);
  const [filterCategoryId, setFilterCategoryId] = useState(null);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [loadingServiceCategories, setLoadingServiceCategories] = useState(false);
  
  // Dialog states
  const [openForm, setOpenForm] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [formMode, setFormMode] = useState('create');
  
  // Snackbar
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Bulk import form dialog
  const EMPTY_ROW = () => ({
    name: '', slug: '', heading: '', description: '',
    image: '', metaTitle: '', metaDescription: '', isActive: true
  });
  const [openImport, setOpenImport] = useState(false);
  const [importRows, setImportRows] = useState([EMPTY_ROW()]);
  const [importing, setImporting] = useState(false);
  const [importRowErrors, setImportRowErrors] = useState([{}]);

  // JS data loader panel
  const [loaderOpen, setLoaderOpen] = useState(false);
  const [loaderCode, setLoaderCode] = useState('');
  const [loaderError, setLoaderError] = useState('');
  const fileInputRef = useRef(null);

  /** Normalize items from any data.js shape into Category fields */
  const normalizeItem = (item) => ({
    name: item.name || '',
    slug: (item.slug || '').toLowerCase().trim(),
    heading: item.heading || item.page?.heading || '',
    description: item.description || '',
    image: item.image || '',
    metaTitle: item.metaTitle || item.meta?.title || '',
    metaDescription: item.metaDescription || item.meta?.description || '',
    isActive: item.isActive !== undefined ? item.isActive : true,
    _slugTouched: true
  });

  /**
   * Parse ES module JS code (export const X = [...]) or plain JSON.
   * Uses Function constructor — safe for trusted internal data.js content.
   */
  const parseJsCode = (code) => {
    let cleaned = code.trim();
    // Strip: export const varName = (handles export default too)
    cleaned = cleaned.replace(/^export\s+(default\s+)?const\s+\w+\s*=\s*/, '');
    cleaned = cleaned.replace(/;\s*$/, '').trim();
    // eslint-disable-next-line no-new-func
    const result = new Function(`return (${cleaned})`)();
    return Array.isArray(result) ? result : [result];
  };

  const handleLoadFromCode = () => {
    setLoaderError('');
    try {
      const arr = parseJsCode(loaderCode);
      const normalized = arr.map(normalizeItem).filter(r => r.name && r.slug);
      if (normalized.length === 0) {
        setLoaderError('No valid items found. Each item must have name and slug.');
        return;
      }
      setImportRows(normalized);
      setImportRowErrors(normalized.map(() => ({})));
      setLoaderOpen(false);
      setLoaderCode('');
    } catch (e) {
      setLoaderError('Could not parse: ' + e.message);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setLoaderCode(ev.target.result);
      setLoaderError('');
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const generateSlug = (text) =>
    text.toLowerCase().trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const handleRowChange = (idx, field, value) => {
    setImportRows(rows => {
      const updated = [...rows];
      updated[idx] = { ...updated[idx], [field]: value };
      if (field === 'name' && !updated[idx]._slugTouched) {
        updated[idx].slug = generateSlug(value);
      }
      if (field === 'slug') updated[idx]._slugTouched = true;
      return updated;
    });
    setImportRowErrors(errs => {
      const updated = [...errs];
      updated[idx] = { ...updated[idx], [field]: '' };
      return updated;
    });
  };

  const handleAddRow = () => {
    setImportRows(r => [...r, EMPTY_ROW()]);
    setImportRowErrors(e => [...e, {}]);
  };

  const handleRemoveRow = (idx) => {
    setImportRows(r => r.filter((_, i) => i !== idx));
    setImportRowErrors(e => e.filter((_, i) => i !== idx));
  };

  const validateImportRows = () => {
    let valid = true;
    const errs = importRows.map(row => {
      const rowErr = {};
      if (!row.name.trim()) { rowErr.name = 'Required'; valid = false; }
      if (!row.slug.trim()) { rowErr.slug = 'Required'; valid = false; }
      return rowErr;
    });
    setImportRowErrors(errs);
    return valid;
  };

  const handleConfirmImport = async () => {
    if (!validateImportRows()) return;
    try {
      setImporting(true);
      const items = importRows.map(({ _slugTouched, ...row }) => row);
      const response = await webCategoryService.bulkImportCategories(items);
      if (response.success) {
        const { created, skipped, errors } = response.data;
        showSnackbar(
          `Import done: ${created} created, ${skipped} skipped${errors.length ? `, ${errors.length} errors` : ''}`,
          errors.length > 0 ? 'warning' : 'success'
        );
        setOpenImport(false);
        setImportRows([EMPTY_ROW()]);
        setImportRowErrors([{}]);
        fetchSubCategories();
      }
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Import failed', 'error');
    } finally {
      setImporting(false);
    }
  };

  const handleCloseImport = () => {
    setOpenImport(false);
    setImportRows([EMPTY_ROW()]);
    setImportRowErrors([{}]);
    setLoaderOpen(false);
    setLoaderCode('');
    setLoaderError('');
  };

  // Fetch service categories for filter dropdown
  useEffect(() => {
    const fetchServiceCategories = async () => {
      try {
        setLoadingServiceCategories(true);
        const response = await webCategoryService.getParentCategoriesForDropdown({ limit: 200, isActive: true });
        if (response.success) {
          setServiceCategories(response.data || []);
        }
      } catch (error) {
        console.error('Error fetching service categories:', error);
      } finally {
        setLoadingServiceCategories(false);
      }
    };
    fetchServiceCategories();
  }, []);

  // Fetch categories for table
  const fetchSubCategories = async () => {
    try {
      setLoading(true);
      const params = {
        limit: 100,
        ...(filterActive !== null && { isActive: filterActive }),
        ...(filterCategoryId !== null && { categoryId: filterCategoryId })
      };

      const response = await webCategoryService.getAllCategories(params);
      
      if (response.success) {
        let data = response.data;
        
        // Apply search filter
        if (searchQuery) {
          data = data.filter(cat => 
            (cat.name || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
            (cat.slug || '').toLowerCase().includes(searchQuery.toLowerCase())
          );
        }
        
        // Pagination
        const startIndex = paginationModel.page * paginationModel.pageSize;
        const endIndex = startIndex + paginationModel.pageSize;
        const paginatedData = data.slice(startIndex, endIndex);
        
        setSubCategories(paginatedData);
        setTotalRows(data.length);
      }
    } catch (error) {
      showSnackbar('Error fetching categories', 'error');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSubCategories();
  }, [paginationModel, searchQuery, filterActive, filterCategoryId]);

  // Handlers
  const handleCreate = () => {
    setFormMode('create');
    setSelectedSubCategory(null);
    setOpenForm(true);
  };

  const handleEdit = (subCategory) => {
    setFormMode('edit');
    setSelectedSubCategory(subCategory);
    setOpenForm(true);
  };

  const handleDelete = (subCategory) => {
    setSelectedSubCategory(subCategory);
    setOpenDelete(true);
  };

  const handleFormClose = () => {
    setOpenForm(false);
    setSelectedSubCategory(null);
  };

  const handleFormSuccess = () => {
    handleFormClose();
    fetchSubCategories();
    showSnackbar(
      `Category ${formMode === 'create' ? 'created' : 'updated'} successfully`,
      'success'
    );
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await webCategoryService.deleteCategory(selectedSubCategory._id);
      if (response.success) {
        setOpenDelete(false);
        fetchSubCategories();
        showSnackbar('Category deleted successfully', 'success');
      }
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error deleting category', 'error');
    }
  };

  const handleToggleDirectLocationPage = async (subCategory) => {
    try {
      const response = await webCategoryService.updateCategory(subCategory._id, {
        ...subCategory,
        directLocationPage: !subCategory.directLocationPage
      });
      
      if (response.success) {
        fetchSubCategories();
        showSnackbar('Direct Location Page updated successfully', 'success');
      }
    } catch (error) {
      showSnackbar('Error updating Direct Location Page', 'error');
    }
  };

  const handleToggleStatus = async (subCategory) => {
    try {
      const response = await webCategoryService.updateCategory(subCategory._id, {
        ...subCategory,
        isActive: !subCategory.isActive
      });
      
      if (response.success) {
        fetchSubCategories();
        showSnackbar('Status updated successfully', 'success');
      }
    } catch (error) {
      showSnackbar('Error updating status', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // DataGrid columns
  const columns = [
    {
      field: 'heading',
      headerName: 'Title',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => params.row.heading || '-'
    },
    {
      field: 'slug',
      headerName: 'Slug',
      flex: 1,
      minWidth: 150,
      hide: isMobile
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 200,
      hide: isMobile
    },
    {
      field: 'directLocationPage',
      headerName: 'Direct Location',
      width: 140,
      renderCell: (params) => (
        <Switch
          checked={!!params.value}
          onChange={() => handleToggleDirectLocationPage(params.row)}
          color="secondary"
        />
      )
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => (
        <Switch
          checked={params.value}
          onChange={() => handleToggleStatus(params.row)}
          color="primary"
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handleEdit(params.row)}
            color="primary"
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row)}
            color="error"
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Box>
      )
    }
  ];

  return (
    <Box>
      <PageHeaderLayout headerTitle="Web Categories">
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => navigate('/admin/web-category-import')}
            sx={{ minWidth: { xs: 'auto', sm: 140 } }}
          >
            {isMobile ? '' : 'Bulk Import'}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            sx={{ minWidth: { xs: 'auto', sm: 140 } }}
          >
            {isMobile ? 'Add' : 'Add Category'}
          </Button>
        </Box>
      </PageHeaderLayout>
      
      <Paper sx={{ p: 2, mt: 2 }}>
        {/* Filters */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
          <TextField
            size="small"
            placeholder="Search categories..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flex: 1, minWidth: 200 }}
          />

          <Autocomplete
            size="small"
            options={serviceCategories}
            getOptionLabel={(opt) => opt.title || opt.name || ''}
            loading={loadingServiceCategories}
            value={serviceCategories.find(c => c.id === filterCategoryId) || null}
            onChange={(_, newVal) => {
              setFilterCategoryId(newVal ? newVal.id : null);
              setPaginationModel(m => ({ ...m, page: 0 }));
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Filter by Service Category"
                placeholder="All categories"
              />
            )}
            sx={{ minWidth: 220 }}
          />

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label="All"
              onClick={() => setFilterActive(null)}
              color={filterActive === null ? 'primary' : 'default'}
              variant={filterActive === null ? 'filled' : 'outlined'}
            />
            <Chip
              label="Active"
              onClick={() => setFilterActive(true)}
              color={filterActive === true ? 'success' : 'default'}
              variant={filterActive === true ? 'filled' : 'outlined'}
            />
            <Chip
              label="Inactive"
              onClick={() => setFilterActive(false)}
              color={filterActive === false ? 'error' : 'default'}
              variant={filterActive === false ? 'filled' : 'outlined'}
            />
          </Box>
        </Box>

        {/* Data Grid */}
        <DataGrid
          rows={subCategories}
          columns={columns}
          getRowId={(row) => row._id}
          pagination
          paginationMode="server"
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          rowCount={totalRows}
          loading={loading}
          pageSizeOptions={[5, 10, 25, 50]}
          disableRowSelectionOnClick
          autoHeight
          sx={{
            '& .MuiDataGrid-cell:focus': {
              outline: 'none'
            }
          }}
        />
      </Paper>

      {/* Form Dialog */}
      <Dialog 
        open={openForm} 
        onClose={handleFormClose}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            m: { xs: 0, sm: 2 },
            maxHeight: { xs: '100%', sm: '90vh' },
            width: { xs: '100%', sm: '90%', md: '80%', lg: '70%' }
          }
        }}
      >
        <DialogTitle sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          fontWeight: 600
        }}>
          {formMode === 'create' ? 'Add New Category' : 'Edit Category'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <SubCategoryForm
            subCategory={selectedSubCategory}
            mode={formMode}
            onSuccess={handleFormSuccess}
            onCancel={handleFormClose}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <DeleteConfirmDialog
        open={openDelete}
        title="Delete Category"
        message={`Are you sure you want to delete "${selectedSubCategory?.name}"? This will also delete all subcategories.`}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setOpenDelete(false)}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Bulk Import Form Dialog */}
      <Dialog
        open={openImport}
        onClose={handleCloseImport}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: { maxHeight: '92vh' } }}
      >
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider', fontWeight: 600 }}>
          Bulk Add Categories
          <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 400, mt: 0.5 }}>
            Fill in the fields below. Name and Slug are required. Slug is auto-generated from Name.
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ p: 3, overflowY: 'auto' }}>
          {/* ── Load from JS/JSON data ── */}
          <Paper
            variant="outlined"
            sx={{ mb: 3, borderRadius: 2, overflow: 'hidden', borderColor: loaderOpen ? 'primary.main' : 'divider' }}
          >
            <Box
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                px: 2, py: 1.5, cursor: 'pointer',
                bgcolor: loaderOpen ? 'primary.50' : 'background.default',
                '&:hover': { bgcolor: 'action.hover' }
              }}
              onClick={() => setLoaderOpen(o => !o)}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CodeIcon fontSize="small" color="primary" />
                <Typography variant="subtitle2" color="primary">
                  Load from JS / JSON data
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  — paste export const array or upload a .js file
                </Typography>
              </Box>
              {loaderOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            </Box>

            <Collapse in={loaderOpen}>
              <Divider />
              <Box sx={{ p: 2 }}>
                <TextField
                  label="Paste JS or JSON array"
                  multiline
                  minRows={6}
                  maxRows={14}
                  fullWidth
                  value={loaderCode}
                  onChange={(e) => { setLoaderCode(e.target.value); setLoaderError(''); }}
                  error={!!loaderError}
                  helperText={loaderError || 'Supports: export const x = [...] or plain [...] JSON'}
                  placeholder={`export const beautyServicesNCR = [\n  { name: "Beauty Services in Gurugram", slug: "gurugram", ... },\n  ...\n]`}
                  inputProps={{ style: { fontFamily: 'monospace', fontSize: 12 } }}
                />
                <Box sx={{ mt: 1.5, display: 'flex', gap: 1.5, alignItems: 'center' }}>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleLoadFromCode}
                    disabled={!loaderCode.trim()}
                    startIcon={<CodeIcon />}
                  >
                    Parse &amp; Fill Form
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AttachFileIcon />}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Upload .js file
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".js,.json"
                    style={{ display: 'none' }}
                    onChange={handleFileUpload}
                  />
                  {loaderCode && (
                    <Typography variant="caption" color="text.secondary">
                      {loaderCode.length.toLocaleString()} chars loaded
                    </Typography>
                  )}
                </Box>
              </Box>
            </Collapse>
          </Paper>
          {importRows.map((row, idx) => (
            <Box key={idx}>
              {idx > 0 && <Divider sx={{ my: 3 }} />}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  Entry #{idx + 1}
                </Typography>
                {importRows.length > 1 && (
                  <Tooltip title="Remove this entry">
                    <IconButton size="small" color="error" onClick={() => handleRemoveRow(idx)}>
                      <RemoveRowIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>

              <Grid container spacing={2}>
                {/* Row 1: Name + Slug */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Name *"
                    size="small"
                    fullWidth
                    value={row.name}
                    onChange={(e) => handleRowChange(idx, 'name', e.target.value)}
                    error={!!importRowErrors[idx]?.name}
                    helperText={importRowErrors[idx]?.name}
                    placeholder="Beauty Services in Gurugram"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Slug *"
                    size="small"
                    fullWidth
                    value={row.slug}
                    onChange={(e) => handleRowChange(idx, 'slug', e.target.value)}
                    error={!!importRowErrors[idx]?.slug}
                    helperText={importRowErrors[idx]?.slug || 'Auto-generated from name'}
                    placeholder="beauty-services-gurugram"
                  />
                </Grid>

                {/* Row 2: Heading + Meta Title */}
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Heading"
                    size="small"
                    fullWidth
                    value={row.heading}
                    onChange={(e) => handleRowChange(idx, 'heading', e.target.value)}
                    placeholder="Beauty Services in Gurugram"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Meta Title"
                    size="small"
                    fullWidth
                    value={row.metaTitle}
                    onChange={(e) => handleRowChange(idx, 'metaTitle', e.target.value)}
                    placeholder="Beauty Services in Gurugram | Salon At Home - CuDel"
                    inputProps={{ maxLength: 60 }}
                    helperText={`${row.metaTitle.length}/60`}
                  />
                </Grid>

                {/* Row 3: Meta Description */}
                <Grid item xs={12}>
                  <TextField
                    label="Meta Description"
                    size="small"
                    fullWidth
                    multiline
                    rows={2}
                    value={row.metaDescription}
                    onChange={(e) => handleRowChange(idx, 'metaDescription', e.target.value)}
                    placeholder="CuDel offers a range of beauty services in Gurugram..."
                    inputProps={{ maxLength: 160 }}
                    helperText={`${row.metaDescription.length}/160`}
                  />
                </Grid>

                {/* Row 4: Description */}
                <Grid item xs={12}>
                  <TextField
                    label="Description"
                    size="small"
                    fullWidth
                    multiline
                    rows={2}
                    value={row.description}
                    onChange={(e) => handleRowChange(idx, 'description', e.target.value)}
                    placeholder="Short description of the category..."
                  />
                </Grid>

                {/* Row 5: Image URL + Active */}
                <Grid item xs={12} sm={9}>
                  <TextField
                    label="Image URL"
                    size="small"
                    fullWidth
                    value={row.image}
                    onChange={(e) => handleRowChange(idx, 'image', e.target.value)}
                    placeholder="https://cudel.s3.amazonaws.com/..."
                  />
                </Grid>
                <Grid item xs={12} sm={3} sx={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={row.isActive}
                        onChange={(e) => handleRowChange(idx, 'isActive', e.target.checked)}
                        color="primary"
                        size="small"
                      />
                    }
                    label="Active"
                  />
                </Grid>
              </Grid>
            </Box>
          ))}

          <Button
            variant="outlined"
            startIcon={<AddRowIcon />}
            onClick={handleAddRow}
            sx={{ mt: 3 }}
            size="small"
          >
            Add Another Entry
          </Button>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
            {importRows.length} entr{importRows.length === 1 ? 'y' : 'ies'} — existing slugs will be skipped
          </Typography>
          <Button onClick={handleCloseImport} disabled={importing}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConfirmImport}
            disabled={importing}
            startIcon={importing ? <CircularProgress size={16} color="inherit" /> : <UploadIcon />}
          >
            {importing ? 'Saving...' : `Save ${importRows.length} Entr${importRows.length === 1 ? 'y' : 'ies'}`}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubCategoryList;
